<script lang="ts">
  import '../app.css';
  import type { Snippet } from 'svelte';
  import { onMount } from 'svelte';
  import { invalidateAll } from '$app/navigation';
  import { initAnalytics } from '$lib/analytics';
  import { removeBootLoader } from '$lib/utils/bootLoader';
  import { createAuthRedirectTimeout } from '$lib/utils/authRedirectTimeout';
  import { applyTheme, getPreferredTheme } from '$lib/stores/themeStore';
  import ErrorState from '$lib/components/shared/ErrorState.svelte';
  import type { LayoutData } from './$types';

  let { children, data }: { children: Snippet; data: LayoutData } = $props();

  let retrying = $state(false);
  // Set when an auth redirect has taken too long, so we stop holding the boot
  // loader and give the user an actionable fallback instead.
  let authRedirectStalled = $state(false);

  // An error to show: either a genuine load error, or a stalled auth redirect.
  const errorMessage = $derived(
    authRedirectStalled
      ? 'Signing you in is taking longer than expected. Please try again.'
      : data.loadError
  );

  const authTimeout = createAuthRedirectTimeout({
    onTimeout: () => {
      // Surface the stalled redirect to monitoring/alerts; this path means the
      // browser never navigated to Keycloak within the timeout.
      console.error('Auth redirect stalled after timeout');
      authRedirectStalled = true;
      // The error state supersedes the boot loader now.
      removeBootLoader();
    }
  });

  async function retry() {
    if (retrying) return;
    retrying = true;
    // A stalled auth redirect means the browser never left for Keycloak; a hard
    // reload is the cleanest recovery (re-runs the full boot + auth handshake).
    if (authRedirectStalled) {
      location.reload();
      return;
    }
    try {
      await invalidateAll();
    } finally {
      retrying = false;
    }
  }

  // Arm/disarm the auth-redirect timeout reactively so retries work: a retry
  // re-runs +layout.ts and updates `data`, and a transition into
  // `authRedirecting` (e.g. network-error → auth redirect) must (re)start the
  // timeout. onMount only runs once and would miss that transition.
  $effect(() => {
    if (data.authRedirecting) {
      // During an auth redirect the browser is about to navigate to Keycloak,
      // so keep the boot loader up (unless/until the redirect stalls).
      authTimeout.start();
    } else {
      // App is ready (or has an error to show); tear the loader down.
      removeBootLoader();
      authTimeout.cancel();
    }

    return () => authTimeout.cancel();
  });

  onMount(() => {
    // Reconcile the theme with the DOM on mount (the inline script in app.html
    // already applied it pre-paint; this keeps things correct after hydration).
    applyTheme(getPreferredTheme());

    // Initialize analytics after the app mounts (auth will be initialized on
    // first API call). We delay slightly to ensure auth is ready from the load.
    const analyticsTimer = setTimeout(() => {
      initAnalytics();
    }, 100);

    return () => {
      clearTimeout(analyticsTimer);
    };
  });
</script>

{#if errorMessage}
  <ErrorState message={errorMessage} onRetry={retry} {retrying} />
{:else}
  {@render children()}
{/if}
